import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { showToast } from '@/components/ui/show-toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { useRegisterMutation } from '@/services/auth/queries';
import { usePostHog } from 'posthog-js/react';

const formSchema = z.object({
  password: z
    .string()
    .min(8, { message: 'Password must be at least 8 characters' })
    .regex(/[A-Za-z]/, { message: 'Must contain at least one letter' })
    .regex(/[0-9]/, { message: 'Must contain at least one number' })
    .regex(/[^A-Za-z0-9]/, {
      message: 'Password must contain at least one special character'
    }),
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type UserFormValue = z.infer<typeof formSchema>;

export default function RegisterForm() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const inviteToken = searchParams.get('token');
  const posthog = usePostHog();

  const form = useForm<UserFormValue>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: '',
      confirmPassword: ''
    }
  });

  const { mutate, isPending } = useRegisterMutation(
    () => {
      posthog?.capture('form_submitted', { form_name: 'register', success: true });
      showToast('success', 'Account created successfully. Redirecting to login page...');
      setTimeout(() => {
        window.location.href = '/sign-in';
      }, 1000);
    },
    (error) => {
      posthog?.capture('form_submitted', { form_name: 'register', success: false });
      if (error.response?.status === 400) {
        showToast('error', 'Invalid invitation token or email');
      } else {
        showToast('error', 'Something went wrong! Please try again later');
      }
    }
  );

  const onSubmit = (data: UserFormValue) => {
    if (!email || !inviteToken) {
      showToast('error', 'Missing email or invitation token');
      return;
    }
    
    mutate({
      email,
      password: data.password,
      inviteToken
    });
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className='w-full space-y-4'
      >
        {email && (
          <div className='mb-4'>
            <p className='text-sm text-gray-600'>
              Creating account for: <span className='font-semibold'>{email}</span>
            </p>
          </div>
        )}
        
        <FormField
          control={form.control}
          name='password'
          render={({ field }) => (
            <FormItem>
              <FormLabel className='text-sm font-bold'>Password</FormLabel>
              <FormControl>
                <Input
                  type='password'
                  placeholder='Enter your password...'
                  disabled={isPending}
                  className='h-[50px]'
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name='confirmPassword'
          render={({ field }) => (
            <FormItem>
              <FormLabel className='text-sm font-bold'>Confirm Password</FormLabel>
              <FormControl>
                <Input
                  type='password'
                  placeholder='Confirm your password...'
                  disabled={isPending}
                  className='h-[50px]'
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type='submit'
          disabled={isPending}
          className='h-12 w-full text-white'
          aria-label='Create Account'
          trackingEvent='register_submitted'
          trackingProperties={{ location: 'register_page' }}
        >
          {isPending ? 'Creating Account...' : 'Create Account'}
        </Button>
      </form>
    </Form>
  );
}
